import { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Text,
  Button,
  InlineStack,
  BlockStack,
  TextField,
  ResourceList,
  Box,
  Checkbox,
  Spinner,
  Icon,
  Tag,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { ErrorResponse } from "@shared/types/commonTypes";
import { SearchIcon } from "@medentic/polaris-icons";
import { formatStartDate } from "@shared/lib";
import { RenderDate } from "@shared/ui";
import { useEvents } from "@features/events/hooks/useEventsDashboard";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import { EventTicket, EventListItem, MultilingualText, EventStatusType } from "@features/events/types";
import { getLocalizedText } from "@shared/lib";
import { Currency } from "@shared/ui";

// Local types for component-specific usage
interface Ticket extends Omit<EventTicket, 'title' | 'description'> {
  title: MultilingualText;
}

interface Payment {
  id: string;
  payerName: string;
  amount: number;
  currency: string;
  date: string;
}

interface PaymentWithNoPayment extends Payment {
  isNoPayment?: boolean;
}

interface AssignPayload {
  eventId: string;
  ticketId: string;
  notify: boolean;
  paymentId?: string;
}

interface AssignToEventModalProps {
  open: boolean;
  onClose: () => void;
  attendeeId?: string;
  attendeeName?: string;
  onSuccess?: () => void;
}

const AssignToEventModal = ({
  open,
  onClose,
  attendeeId,
  attendeeName,
  onSuccess,
}: AssignToEventModalProps) => {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET, POST } = useApiRequest();

  // Modal state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Event selection
  const [eventSearchQuery, setEventSearchQuery] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<EventStatusType[]>([
    EventStatusType.ONGOING,
    EventStatusType.UPCOMING,
  ]);
  const [selectedEvent, setSelectedEvent] = useState<EventListItem | null>(null);

  // Use events hook with dynamic status
  const { data: eventsData = [], isLoading: eventsLoading } = useEvents(
    hcc,
    selectedStatusFilter.length === 0 ? undefined : selectedStatusFilter
  );
  const allEvents = eventsData as EventListItem[];

  // Filter events based on search query
  const filteredEvents = allEvents.filter((event: EventListItem) => {
    if (!eventSearchQuery.trim()) return true;
    const searchLower = eventSearchQuery.toLowerCase();
    return (
      event.name.toLowerCase().includes(searchLower) ||
      event.publicTitle.toLowerCase().includes(searchLower)
    );
  });

  // Step 2: Ticket selection
  const [availableTickets, setAvailableTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [ticketSearchQuery, setTicketSearchQuery] = useState("");

  // Filter tickets based on search query
  const filteredTickets = availableTickets.filter((ticket: Ticket) => {
    if (!ticketSearchQuery.trim()) return true;
    const searchLower = ticketSearchQuery.toLowerCase();
    return getLocalizedText(ticket.title)
      .toLowerCase()
      .includes(searchLower);
  });

  // Step 3: Payment selection
  const [availablePayments, setAvailablePayments] = useState<Payment[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [noPaymentSelected, setNoPaymentSelected] = useState(false);

  // Step 4: Summary
  const [notifyAttendee, setNotifyAttendee] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);

  // No need for filtering useEffect since we handle it directly in the component

  // Load tickets when event is selected
  useEffect(() => {
    if (selectedEvent && currentStep === 2) {
      loadTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, currentStep]);

  // Load payments when ticket is selected
  useEffect(() => {
    if (selectedEvent && selectedTicket && currentStep === 3) {
      loadPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, selectedTicket, currentStep]);

  // loadEvents function removed - now using useEvents hook

  const loadTickets = async () => {
    if (!hcc || !selectedEvent) return;

    setLoading(true);
    setError(null);

    try {
      const tickets = await GET(
        `/api/healthcare-companies/${hcc}/e/events/${selectedEvent.id}/tickets`
      );
      setAvailableTickets((tickets as Ticket[]) || []);
    } catch {
      setError(t("assignToEvent.failedToLoadTickets"));
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    if (!hcc || !selectedEvent || !selectedTicket) return;

    setLoading(true);
    setError(null);

    try {
      const payments = await GET(
        `/api/healthcare-companies/${hcc}/e/finance/transactions?eventId=${selectedEvent.id}&ticketId=${selectedTicket.id}&missingAttendee=1`
      );
      setAvailablePayments((payments as Payment[]) || []);
    } catch {
      setError(t("assignToEvent.failedToLoadPayments"));
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!hcc || !attendeeId || !selectedEvent || !selectedTicket) return;

    setIsAssigning(true);
    setError(null);

    try {
      const payload: AssignPayload = {
        eventId: selectedEvent.id,
        ticketId: selectedTicket.id,
        notify: notifyAttendee,
      };

      if (selectedPayment) {
        payload.paymentId = selectedPayment.id;
      }

      await POST(
        `/api/healthcare-companies/${hcc}/e/attendees/${attendeeId}/ticket`,
        payload
      );

      onSuccess?.();
      onClose();
      resetModal();
    } catch (err) {
      const displayMsg = (err as ErrorResponse)?.response?.data?.error?.display;
      setError(displayMsg || t("assignToEvent.failedToAssign"));
    } finally {
      setIsAssigning(false);
    }
  };

  const resetModal = useCallback(() => {
    setCurrentStep(1);
    setEventSearchQuery("");
    setSelectedStatusFilter([EventStatusType.ONGOING, EventStatusType.UPCOMING]);
    setSelectedEvent(null);
    setAvailableTickets([]);
    setSelectedTicket(null);
    setTicketSearchQuery("");
    setAvailablePayments([]);
    setSelectedPayment(null);
    setNoPaymentSelected(false);
    setNotifyAttendee(true);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    resetModal();
  }, [onClose, resetModal]);

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return t("assignToEvent.step1.title");
      case 2:
        return t("assignToEvent.step2.title", {
          eventTitle: selectedEvent?.publicTitle,
        });
      case 3:
        return t("assignToEvent.step3.title");
      case 4:
        return t("assignToEvent.step4.title");
      default:
        return t("assignToEvent.title");
    }
  };

  // Small presentation component to format ticket price
  const renderStep1 = () => (
    <BlockStack gap="400">
      <Text as="p" tone="subdued">
        {t("assignToEvent.step1.description")}
      </Text>

      <TextField
        label=""
        value={eventSearchQuery}
        onChange={setEventSearchQuery}
        placeholder={t("assignToEvent.step1.searchPlaceholder")}
        prefix={<Icon source={SearchIcon} />}
        autoComplete="off"
      />

      {selectedStatusFilter.length > 0 && (
        <BlockStack gap="200">
          <InlineStack gap="200" align="start">
            <Tag onRemove={() => setSelectedStatusFilter([])}>
              {t("assignToEvent.step1.statusFilter")}
            </Tag>

            <Button
              variant="plain"
              onClick={() => setSelectedStatusFilter([])}
              size="slim"
            >
              {t("assignToEvent.step1.clearAll")}
            </Button>
          </InlineStack>
        </BlockStack>
      )}

      {eventsLoading ? (
        <Box padding="400">
          <InlineStack gap="200" blockAlign="center" align="center">
            <Spinner size="small" />
            <Text as="span">{t("assignToEvent.loadingEvents")}</Text>
          </InlineStack>
        </Box>
      ) : (
        <ResourceList
          resourceName={{
            singular: t("assignToEvent.event"),
            plural: t("assignToEvent.events"),
          }}
          items={filteredEvents}
          renderItem={(event) => (
            <ResourceList.Item
              id={event.id}
              onClick={() => setSelectedEvent(event)}
            >
              <Box
                padding="200"
                background={
                  selectedEvent?.id === event.id
                    ? "bg-surface-selected"
                    : undefined
                }
                borderRadius="200"
              >
                <InlineStack align="space-between" blockAlign="center">
                  <Box maxWidth="80%">
                    <BlockStack gap="050">
                      <Text
                        variant="bodyMd"
                        fontWeight="medium"
                        as="p"
                        truncate
                      >
                        {event.publicTitle}
                      </Text>
                      <Text variant="bodySm" tone="subdued" as="p">
                        {event.dateStart === event.dateEnd &&
                        event.dateStart ? (
                          <RenderDate dayFirst={true} date={event.dateStart} />
                        ) : (
                          <>
                            {formatStartDate(event.dateStart)}
                            {event.dateEnd && (
                              <>
                                {" "}
                                -{" "}
                                <RenderDate
                                  dayFirst={true}
                                  date={event.dateEnd}
                                />
                              </>
                            )}
                          </>
                        )}
                      </Text>
                    </BlockStack>
                  </Box>

                  <EventBadgeRenderer slug={event.status || ''} entity="event" />
                </InlineStack>
              </Box>
            </ResourceList.Item>
          )}
        />
      )}
    </BlockStack>
  );

  const renderStep2 = () => (
    <BlockStack gap="400">
      <Text as="p" tone="subdued">
        {t("assignToEvent.step2.description")}
      </Text>

      <TextField
        label=""
        value={ticketSearchQuery}
        onChange={setTicketSearchQuery}
        placeholder={t("assignToEvent.step2.searchPlaceholder")}
        prefix={<Icon source={SearchIcon} />}
        autoComplete="off"
      />

      {loading ? (
        <Box padding="400">
          <InlineStack gap="200" blockAlign="center" align="center">
            <Spinner size="small" />
            <Text as="span">{t("assignToEvent.loadingTickets")}</Text>
          </InlineStack>
        </Box>
      ) : (
        <ResourceList
          resourceName={{
            singular: t("assignToEvent.ticket"),
            plural: t("assignToEvent.tickets"),
          }}
          items={filteredTickets}
          renderItem={(ticket) => (
            <ResourceList.Item
              id={ticket.id}
              onClick={() => {
                if (ticket.status !== "archived") setSelectedTicket(ticket);
              }}
            >
              <Box
                padding="400"
                borderRadius="0"
                background={
                  selectedTicket?.id === ticket.id
                    ? "bg-surface-selected"
                    : undefined
                }
                opacity={ticket.status === "archived" ? "0.5" : undefined}
              >
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="200">
                    <Text variant="bodyMd" fontWeight="medium" as="p">
                      {getLocalizedText(ticket.title)}
                    </Text>
                    <Text variant="bodySm" as="p" fontWeight="medium">
                      <Currency
                        value={ticket.price}
                        currency={ticket.currency}
                      />
                    </Text>
                  </BlockStack>
                  <EventBadgeRenderer slug={ticket.status} entity="ticket" />
                </InlineStack>
              </Box>
            </ResourceList.Item>
          )}
        />
      )}
    </BlockStack>
  );

  const renderStep3 = () => (
    <BlockStack gap="400">
      <Text as="p" tone="subdued">
        {t("assignToEvent.step3.description")}
      </Text>

      <ResourceList
        resourceName={{
          singular: t("assignToEvent.payment"),
          plural: t("assignToEvent.payments"),
        }}
        items={[
          {
            id: "no-payment",
            payerName: t("assignToEvent.noOnlinePayment"),
            amount: 0,
            currency: "",
            date: "",
            isNoPayment: true,
          },
          ...availablePayments,
        ]}
        renderItem={(payment: PaymentWithNoPayment) => (
          <ResourceList.Item
            id={payment.id}
            onClick={() => {
              if (payment.isNoPayment) {
                setSelectedPayment(null);
                setNoPaymentSelected(true);
              } else {
                setSelectedPayment(payment);
                setNoPaymentSelected(false);
              }
            }}
          >
            <Box
              padding="200"
              background={
                (noPaymentSelected && payment.isNoPayment) ||
                selectedPayment?.id === payment.id
                  ? "bg-surface-selected"
                  : undefined
              }
              borderRadius="200"
            >
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <Text variant="bodyMd" fontWeight="medium" as="p">
                    {payment.payerName}
                  </Text>
                  {!payment.isNoPayment && (
                    <>
                      <Text variant="bodySm" tone="subdued" as="p">
                        <Currency value={payment.amount} currency={payment.currency} />
                      </Text>
                      <Text variant="bodySm" tone="subdued" as="p">
                        {payment.date}
                      </Text>
                    </>
                  )}
                  {payment.isNoPayment && (
                    <Text variant="bodySm" tone="subdued" as="p">
                      {t("assignToEvent.attendeePaidAnotherWay")}
                    </Text>
                  )}
                </BlockStack>
              </InlineStack>
            </Box>
          </ResourceList.Item>
        )}
      />
    </BlockStack>
  );

  const renderStep4 = () => (
    <BlockStack gap="400">
      <BlockStack gap="200">
        <InlineStack gap="200">
          <Text variant="bodyMd" fontWeight="medium" as="span">
            {t("assignToEvent.step4.assignee")}:
          </Text>
          <Text variant="bodyMd" as="span">
            {attendeeName}
          </Text>
        </InlineStack>

        <InlineStack gap="200">
          <Text variant="bodyMd" fontWeight="medium" as="span">
            {t("assignToEvent.step4.event")}:
          </Text>
          <Text variant="bodyMd" as="span">
            {selectedEvent?.publicTitle} on{" "}
            {selectedEvent?.dateStart === selectedEvent?.dateEnd &&
            selectedEvent?.dateStart ? (
              <RenderDate dayFirst={true} date={selectedEvent.dateStart} />
            ) : (
              <>
                {selectedEvent?.dateStart &&
                  formatStartDate(selectedEvent.dateStart)}
                {selectedEvent?.dateEnd && (
                  <>
                    {" "}
                    -{" "}
                    <RenderDate dayFirst={true} date={selectedEvent.dateEnd} />
                  </>
                )}
              </>
            )}
          </Text>
        </InlineStack>

        <InlineStack gap="200">
          <Text variant="bodyMd" fontWeight="medium" as="span">
            {t("assignToEvent.step4.ticket")}:
          </Text>
          <Text variant="bodyMd" as="span">
            {getLocalizedText(selectedTicket?.title)}
          </Text>
        </InlineStack>

        <InlineStack gap="200">
          <Text variant="bodyMd" fontWeight="medium" as="span">
            {t("assignToEvent.step4.payment")}:
          </Text>
          <Text variant="bodyMd" as="span">
            {noPaymentSelected
              ? t("assignToEvent.noOnlinePayment")
              : selectedPayment
              ? `${selectedPayment.payerName} on ${
                  selectedPayment.date
                } for ${selectedPayment.amount.toLocaleString()} ${
                  selectedPayment.currency
                }`
              : t("assignToEvent.noPaymentSelected")}
          </Text>
        </InlineStack>
      </BlockStack>

      <Checkbox
        label={t("assignToEvent.step4.sendRegistrationEmails")}
        checked={notifyAttendee}
        onChange={setNotifyAttendee}
      />
    </BlockStack>
  );

  const getCurrentStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return null;
    }
  };

  const getNextButtonContent = () => {
    switch (currentStep) {
      case 4:
        return t("assignToEvent.confirm");
      default:
        return t("assignToEvent.next");
    }
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 1:
        return !selectedEvent;
      case 2:
        return !selectedTicket;
      case 3:
        return !selectedPayment && !noPaymentSelected;
      case 4:
        return false;
      default:
        return true;
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={getStepTitle()}
      primaryAction={{
        content: getNextButtonContent(),
        onAction: currentStep === 4 ? handleAssign : handleNext,
        disabled: isNextDisabled(),
        loading: currentStep === 4 ? isAssigning : false,
      }}
      secondaryActions={
        currentStep > 1
          ? [
              {
                content: t("assignToEvent.back"),
                onAction: handleBack,
                disabled: isAssigning,
              },
            ]
          : []
      }
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        {error && (
          <Box paddingBlockEnd="400">
            <Text as="p" tone="critical">
              {error}
            </Text>
          </Box>
        )}
        {getCurrentStepContent()}
      </Modal.Section>
    </Modal>
  );
};

export default AssignToEventModal;
